import {
  deleteAxiosOrganization,
  getAxiosOrganizations,
  postAxiosCreateOrganization,
  putAxiosOrganization,
} from "@API/Organizations/endpoints";
import {
  IAxiosOrganization,
  IGetAxiosOrganizations,
  IPostAxiosOrganization,
  NewOrganizationPayload,
} from "@API/Organizations/types";
import { usersListRequest } from "@Store/Users/actions";
import i18n from "@i18n";
import { PayloadAction } from "@reduxjs/toolkit";
import { notification } from "antd";
import { AxiosError, isAxiosError } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  createOrganizationFail,
  createOrganizationRequest,
  createOrganizationSuccess,
  deleteOrganizationFail,
  deleteOrganizationRequest,
  deleteOrganizationSuccess,
  organizationsListFail,
  organizationsListRequest,
  organizationsListSuccess,
  resetCreateOrganizationModal,
  systemUpdateOrganizationFail,
  systemUpdateOrganizationRequest,
  systemUpdateOrganizationSuccess,
  updateOrganizationFail,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateSelectedOrganizationId,
} from "./actions";
import { IOrganization, OrganizationId } from "./types";

function* onOrganizationsListRequest() {
  try {
    const response: IGetAxiosOrganizations = yield call(getAxiosOrganizations);

    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(organizationsListSuccess(response.data));

    if (response.data.length > 0) {
      yield put(updateSelectedOrganizationId(response.data[0].orgId));
    }
  } catch (e) {
    console.error(e);
    yield put(organizationsListFail(e as Error));
  }
}

function* onUpdateOrganizationRequest({
  payload,
}: PayloadAction<IOrganization>) {
  try {
    const response: IAxiosOrganization = yield call(
      putAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(updateOrganizationSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(updateOrganizationFail(e as Error));
  }
}

function* onSystemUpdateOrganizationRequest({
  payload,
}: PayloadAction<IOrganization>) {
  try {
    const response: IAxiosOrganization = yield call(
      putAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(systemUpdateOrganizationSuccess(response.data));
  } catch (e) {
    console.error(e);
    yield put(systemUpdateOrganizationFail(e as Error));
  }
}

function* onUpdateSelectedOrganizationId({
  payload,
}: PayloadAction<OrganizationId>) {
  yield put(usersListRequest(payload));
}

// system user requests
function* onCreateOrganizationRequest({
  payload,
}: PayloadAction<NewOrganizationPayload>) {
  try {
    const response: IPostAxiosOrganization = yield call(
      postAxiosCreateOrganization,
      payload
    );

    if (response.error) throw response.error;

    yield put(resetCreateOrganizationModal());
    yield put(createOrganizationSuccess(response.data));

    notification.success({
      message: i18n.t("organization.modals.addOrganization.successMessage"),
    });
  } catch (e) {
    const errorMessage =
      isAxiosError(e) && e.response
        ? e.response.data.message
        : (e as AxiosError).message;

    yield put(createOrganizationFail(e as Error));

    notification.error({
      message: i18n.t("organization.modals.addOrganization.failMessage"),
      description: errorMessage,
    });
  }
}

function* onDeleteOrganizationRequest({
  payload,
}: PayloadAction<OrganizationId>) {
  try {
    const response: { error?: Error } = yield call(
      // @ts-ignore
      deleteAxiosOrganization,
      payload
    );
    if (response.error) throw response.error;

    yield new Promise((resolve) => setTimeout(resolve, 500));
    yield put(deleteOrganizationSuccess(payload));
  } catch (e) {
    console.error(e);
    yield put(deleteOrganizationFail(e as Error));
  }
}

export default function* () {
  yield takeLatest(organizationsListRequest, onOrganizationsListRequest);
  yield takeLatest(
    updateSelectedOrganizationId,
    onUpdateSelectedOrganizationId
  );
  yield takeLatest(updateOrganizationRequest, onUpdateOrganizationRequest);
  yield takeLatest(createOrganizationRequest, onCreateOrganizationRequest);
  yield takeLatest(deleteOrganizationRequest, onDeleteOrganizationRequest);
  yield takeLatest(
    systemUpdateOrganizationRequest,
    onSystemUpdateOrganizationRequest
  );
}
